import { fg } from '@atlaskit/platform-feature-flags';

import {
	type ExperimentExperience,
	type LayerFunction,
	type LinkedEntityGranularity,
} from '../../../schemas/dynamic-config';
import type {
	BackendExperiment,
	CombinedLayerExperiment,
	MutuallyExclusiveDynamicExperiment,
} from '../../../types';
import {
	STATSIG_SEARCH_BACKEND_L1_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_L2_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_L3_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER_NAME,
} from '../../constants';
import { getLayerValues } from '../backend';

import type {
	DynamicExperiments,
	EntityFilters,
	GetLayerValuesFn,
	ProductDataRegions,
	ProductEdition,
	SearchExperimentLayer,
	ThirdPartyFiltersForExperiment,
} from './types';

const WILDCARD = '*';
const JIRA_ENTITY = 'jira';

export const mapCombinedLayerExperiment = ({
	combinedExperiment,
}: {
	combinedExperiment: CombinedLayerExperiment;
}): BackendExperiment => {
	if (combinedExperiment) {
		const experimentLayers = [
			[STATSIG_SEARCH_BACKEND_L1_LAYER_NAME, combinedExperiment.l1Id],
			[STATSIG_SEARCH_BACKEND_L2_LAYER_NAME, combinedExperiment.l2Id],
			[STATSIG_SEARCH_BACKEND_L3_LAYER_NAME, combinedExperiment.l3Id],
			[STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER_NAME, combinedExperiment.qrId],
		];

		return {
			experimentLayers: experimentLayers.map(([layerType, layerId]) => {
				let entity = WILDCARD;
				if (layerType === STATSIG_SEARCH_BACKEND_L1_LAYER_NAME) {
					entity = combinedExperiment.l1Entity ?? WILDCARD;
				}
				if (layerType === STATSIG_SEARCH_BACKEND_L2_LAYER_NAME) {
					entity = combinedExperiment.l2Entity ?? WILDCARD;
				}

				return {
					name: layerType,
					layerId: null,
					shadowId: null,
					definitions: [
						{
							layerId,
							shadowId: null,
							entity,
							subEntity: WILDCARD,
							abTestId: combinedExperiment.abTestId ?? null,
						},
					],
					analyticsDefinitions: [
						{
							layerId,
							shadowId: null,
							entity,
							subEntity: WILDCARD,
							abTestId: combinedExperiment.abTestId ?? null,
							controlId: combinedExperiment.controlId ?? null,
						},
					],
				};
			}),
		};
	}
	return {
		experimentLayers: [],
	};
};

const getLayersRelevantToSearchRequest = <T extends LayerFunction>({
	dynamicExperiments,
	requestedEntities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
	linkedEntityGranularity,
	thirdPartyFilters,
}: {
	dynamicExperiments: T[];
	requestedEntities: EntityFilters;
	productEdition?: ProductEdition;
	isHello?: boolean;
	isJiraIssueIndexed?: boolean;
	linkedEntityGranularity?: LinkedEntityGranularity;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
	thirdPartyFilters?: ThirdPartyFiltersForExperiment;
}): T[] => {
	return dynamicExperiments
		.filter(containsTenantId(tenantId))
		.filter(isSameConfluenceTier(productEdition))
		.filter(containsExperience(experience))
		.filter(isHelloTenant(isHello))
		.filter(containsRequestedEntities(requestedEntities))
		.filter(containsRequestedSubEntities(requestedEntities))
		.filter(contains3pTarget(thirdPartyFilters))
		.filter(isJiraIssueIndexedConditionSatisfied(isJiraIssueIndexed))
		.filter(isLinkedEntityGranularityConditionSatisfied(linkedEntityGranularity))
		.filter(passesTargetingGate())
		.filter(satisfiesProductDataRegions(productDataRegions))
		.reduce<T[]>((layerFunctions, currentLayerFunction) => {
			// IF there are more than one definitions with the same definition
			// THEN only add the first one defined, (do not add this one)
			const containsCollision = layerFunctions.find((layerFunction) => {
				if (fg('rovo_3p_exp_targeting')) {
					/**
					 * This isn't perfect either because there can be some edge cases where
					 * an experiment definition is included, but not used. e.g.
					 * - Defitions we have are:
					 * 			[entity: confluence, subEntity: page],
					 * 			[entity: confluence, subEntity: *]
					 * - The request is for entity: confluence, subEntity: page
					 *
					 * Both definitions would be included in the final list, but only the
					 * first would ever be used, but we fire exposures for both.
					 *
					 * To fix this, we would need to do one of the following:
					 * - Replicate all of the fan out logic here (ugly and hard to maintain),
					 *   so we know exactly which experiment definitions will get used
					 * - Fire experiment exposures in the backend
					 */
					return (
						layerFunction.layerType === currentLayerFunction.layerType &&
						layerFunction.entity === currentLayerFunction.entity &&
						layerFunction.subEntity === currentLayerFunction.subEntity &&
						layerFunction.integrationAri === currentLayerFunction.integrationAri &&
						layerFunction.providerId === currentLayerFunction.providerId &&
						JSON.stringify(layerFunction.connectorSources?.sort()) ===
							JSON.stringify(currentLayerFunction.connectorSources?.sort())
					);
				} else {
					/**
					 * I think there's a bug here, what if:
					 * - layerFunctions contains
					 * 			[entity: graph, subEntity: video]
					 * 			[entity: graph, subEntity: *]
					 * - The request is for entities:
					 * 			ati:cloud:graph:design
					 * 			ati:cloud:graph:video
					 *
					 * Only the first definition would get included, but both would have
					 * been used after the 3P requests are fanned out.
					 */
					return (
						layerFunction.layerType === currentLayerFunction.layerType &&
						(layerFunction.entity === currentLayerFunction.entity ||
							currentLayerFunction.entity === WILDCARD ||
							layerFunction.entity === WILDCARD) &&
						(layerFunction.subEntity === currentLayerFunction.subEntity ||
							currentLayerFunction.subEntity === WILDCARD ||
							layerFunction.subEntity === WILDCARD)
					);
				}
			});
			return containsCollision ? layerFunctions : [...layerFunctions, currentLayerFunction];
		}, []);
};

const mapExperimentsToExperimentDefinitions = (
	experiments: {
		definition: {
			providerId?: string | undefined;
			integrationARI?: string | undefined;
			connectorSources?: string[] | undefined;
			layerId: string | null;
			shadowId: string | null;
			entity: string | undefined;
			subEntity: string | undefined;
			abTestId: string | null;
			controlId: string | null;
			statsigLayerId: string | null;
		};
		layerType: string | undefined;
	}[],
): SearchExperimentLayer[] => {
	return experiments.reduce<SearchExperimentLayer[]>(
		(experimentLayers, definitionWithLayerType) => {
			const existingLayer = experimentLayers.find(
				(layer) => layer.name === definitionWithLayerType.layerType,
			);
			const {
				controlId: _controlId,
				statsigLayerId: _statsigLayerId,
				...newDefinition
			} = definitionWithLayerType.definition;
			const analyticsDefinition = {
				...definitionWithLayerType.definition,
			};

			if (!existingLayer) {
				experimentLayers.push({
					name: definitionWithLayerType.layerType,
					layerId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
					shadowId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
					definitions: [newDefinition],
					analyticsDefinitions: [analyticsDefinition],
				});
				return experimentLayers;
			}

			existingLayer.definitions?.push(newDefinition);
			existingLayer.analyticsDefinitions?.push(analyticsDefinition);

			return experimentLayers;
		},
		[],
	);
};

export const mapMutuallyExclusiveExperiment = ({
	experimentParams,
	requestedEntities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
	linkedEntityGranularity,
	thirdPartyFilters,
}: {
	experimentParams: MutuallyExclusiveDynamicExperiment;
	requestedEntities: EntityFilters;
	productEdition?: ProductEdition;
	isHello?: boolean;
	isJiraIssueIndexed?: boolean;
	linkedEntityGranularity?: LinkedEntityGranularity;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
	thirdPartyFilters?: ThirdPartyFiltersForExperiment;
}): BackendExperiment => {
	const dynamicExperiments = experimentParams?.layerFunctions || [];
	const { abTestId } = experimentParams || { abTestId: null };

	const relevantExperiments = getLayersRelevantToSearchRequest({
		dynamicExperiments,
		requestedEntities,
		productEdition,
		isHello,
		productDataRegions,
		experience,
		tenantId,
		isJiraIssueIndexed,
		linkedEntityGranularity,
		thirdPartyFilters,
	});

	const experiments = relevantExperiments.map((dynamicExp) => {
		return {
			definition: {
				layerId: dynamicExp.layerId,
				shadowId: null,
				entity: dynamicExp.entity,
				subEntity: dynamicExp.subEntity,
				abTestId,
				controlId: dynamicExp.controlId ?? null,
				statsigLayerId: null,
				...(fg('rovo_3p_exp_targeting')
					? {
							providerId: dynamicExp.providerId,
							integrationARI: dynamicExp.integrationAri,
							connectorSources: dynamicExp.connectorSources,
						}
					: {}),
			},
			layerType: dynamicExp.layerType,
		};
	});

	return {
		experimentLayers: mapExperimentsToExperimentDefinitions(experiments),
	};
};

export type BackendExperimentLayerOverride = {
	name: string;
	analyticsDefinitions?: NonNullable<SearchExperimentLayer['analyticsDefinitions']>;
};

export const mergeBackendExperimentLayerOverrides = (
	experimentLayers: SearchExperimentLayer[],
	overrides: BackendExperimentLayerOverride[],
): SearchExperimentLayer[] => {
	const mergedLayers = experimentLayers.map((l) => ({
		...l,
		// ensure that definitions and analyticsDefinitions have an array
		definitions: l.definitions ? [...l.definitions] : [],
		analyticsDefinitions: l.analyticsDefinitions ? [...l.analyticsDefinitions] : [],
	}));

	for (const override of overrides) {
		const overrideDefinitions = override.analyticsDefinitions ?? [];
		if (overrideDefinitions.length === 0) {
			continue;
		}

		const layer = mergedLayers.find((l) => l.name === override.name);
		if (!layer) {
			// Remove controlId from definitions since they're for request payloads
			const requestDefinitions = overrideDefinitions.map(
				({ controlId: _controlId, ...def }) => def,
			);
			mergedLayers.push({
				name: override.name,
				layerId: null,
				shadowId: null,
				definitions: requestDefinitions,
				analyticsDefinitions: [...overrideDefinitions],
			});
			continue;
		}

		for (const overrideDef of overrideDefinitions) {
			const keyEntity = overrideDef.entity ?? null;
			const keySubEntity = overrideDef.subEntity ?? null;

			const analyticsIndex = layer.analyticsDefinitions.findIndex(
				(d) => (d.entity ?? null) === keyEntity && (d.subEntity ?? null) === keySubEntity,
			);
			if (analyticsIndex >= 0) {
				layer.analyticsDefinitions[analyticsIndex] = overrideDef;
			} else {
				layer.analyticsDefinitions.push(overrideDef);
			}

			const { controlId: _controlId, ...requestDef } = overrideDef;
			const requestIndex = layer.definitions.findIndex(
				(d) => (d.entity ?? null) === keyEntity && (d.subEntity ?? null) === keySubEntity,
			);
			if (requestIndex >= 0) {
				layer.definitions[requestIndex] = requestDef;
			} else {
				layer.definitions.push(requestDef);
			}
		}
	}

	return mergedLayers;
};

export const mapDynamicExperiments = ({
	dynamicExperiments,
	requestedEntities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
	linkedEntityGranularity,
	thirdPartyFilters,
	fireLayerExposure = true,
	getLayerValuesFn = getLayerValues,
}: {
	dynamicExperiments: DynamicExperiments;
	requestedEntities: EntityFilters;
	productEdition?: ProductEdition;
	isHello?: boolean;
	isJiraIssueIndexed?: boolean;
	linkedEntityGranularity?: LinkedEntityGranularity;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
	thirdPartyFilters?: ThirdPartyFiltersForExperiment;
	fireLayerExposure?: boolean;
	/** Optional layer value resolver — defaults to the browser FeatureGates implementation.
	 *  SSR can inject its own adapter to resolve layer values from the node client. */
	getLayerValuesFn?: GetLayerValuesFn;
}): BackendExperiment => {
	const relevantExperiments = getLayersRelevantToSearchRequest({
		dynamicExperiments,
		requestedEntities,
		productEdition,
		isHello,
		productDataRegions,
		experience,
		tenantId,
		isJiraIssueIndexed,
		linkedEntityGranularity,
		thirdPartyFilters,
	});

	const experiments = relevantExperiments.map((dynamicExp) => {
		const { layerId, shadowId, abTestId, controlId } = getLayerValuesFn(
			dynamicExp.statsigLayerId,
			fireLayerExposure ? undefined : { fireLayerExposure: false },
		);
		return {
			definition: {
				layerId,
				shadowId,
				entity: dynamicExp.entity,
				subEntity: dynamicExp.subEntity,
				abTestId,
				controlId,
				statsigLayerId: dynamicExp.statsigLayerId,
				...(fg('rovo_3p_exp_targeting')
					? {
							providerId: dynamicExp.providerId,
							integrationARI: dynamicExp.integrationAri,
							connectorSources: dynamicExp.connectorSources,
						}
					: {}),
			},
			layerType: dynamicExp.layerType,
		};
	});

	return {
		experimentLayers: mapExperimentsToExperimentDefinitions(experiments),
	};
};

export const isSameConfluenceTier =
	(productEdition?: ProductEdition) =>
	(dynamicExp: LayerFunction): boolean => {
		// Opt for specificity. Does not filter out values that do not have the confluenceTier attribute
		if (!dynamicExp.confluenceTier || dynamicExp.confluenceTier.length === 0) {
			return true;
		}
		return !!dynamicExp.confluenceTier?.find(
			(experimentTier) => productEdition?.['confluence'] === experimentTier,
		);
	};

const isHelloTenant = (isHello?: boolean) => (dynamicExp: LayerFunction) => {
	if (dynamicExp.hello === undefined || dynamicExp.hello === null) {
		return true;
	}

	return dynamicExp.hello === isHello;
};

const passesTargetingGate = () => (dynamicExp: LayerFunction) => {
	if (dynamicExp.targetingGateId === undefined || dynamicExp.targetingGateId === null) {
		return true;
	}

	// eslint-disable-next-line @atlaskit/platform/static-feature-flags
	return fg(dynamicExp.targetingGateId);
};

/**
 * This is specifically used for targeting based on indexing status of the tenant.
 * This might NOT be needed once Jira indexed and ROVO in Jira is shipped to 100%
 */
const isJiraIssueIndexedConditionSatisfied =
	(isJiraIssueIndexed?: boolean) => (dynamicExp: LayerFunction) => {
		if (
			isJiraIssueIndexed === undefined ||
			dynamicExp.isJiraIssueIndexed === undefined ||
			dynamicExp.isJiraIssueIndexed === null ||
			dynamicExp?.entity !== JIRA_ENTITY
		) {
			return true;
		}

		return dynamicExp.isJiraIssueIndexed === isJiraIssueIndexed;
	};

const isLinkedEntityGranularityConditionSatisfied =
	(linkedEntityGranularity?: LinkedEntityGranularity) => (dynamicExp: LayerFunction) => {
		if (
			linkedEntityGranularity === undefined ||
			dynamicExp.linkedEntityGranularity === undefined ||
			dynamicExp.linkedEntityGranularity === null
		) {
			return true;
		}

		return dynamicExp.linkedEntityGranularity === linkedEntityGranularity;
	};

const containsRequestedEntities = (entityFilter: EntityFilters) => (dynamicExp: LayerFunction) => {
	if (!dynamicExp.entity || dynamicExp.entity === WILDCARD) {
		return true;
	}

	const isFilteredEntity = entityFilter.find(
		(filteredEntity) => dynamicExp.entity === filteredEntity.entity,
	);
	return isFilteredEntity ?? false;
};

const containsRequestedSubEntities =
	(entityFilter: EntityFilters) => (dynamicExp: LayerFunction) => {
		if (!dynamicExp.subEntity || dynamicExp.subEntity === WILDCARD) {
			return true;
		}

		const isFilteredSubEntity = entityFilter.find(
			(filteredEntity) => dynamicExp.subEntity === filteredEntity.subEntity,
		);
		return isFilteredSubEntity ?? false;
	};

/**
 * Determines whether the provided data regions (dataRegions) are defined in
 * the dynamic configuration (dynamicExp). Every data region for a given product
 * in the dynamic configuration must be specified  in order for the given
 * experiment layer to be eligible.
 *
 * @param dataRegions Represents where the data comes from comes from for
 * each product.
 * ```
 * { 'confluence': ["us-west-2"], 'compass': ["us-east-1"] }`
 * ```
 *
 * @returns a boolean that indicates whether or not the provided data regions
 * (dataRegions) are specified in the dynamic configuration (dynamicExp).
 */
const satisfiesProductDataRegions =
	(dataRegions?: ProductDataRegions) => (dynamicExp: LayerFunction) => {
		if (!dynamicExp.productDataRegions) {
			return true;
		}

		return Object.keys(dynamicExp.productDataRegions)
			.map((product) => {
				const productDataRegions = dynamicExp.productDataRegions?.[product];
				const isProductEligible = dataRegions?.[product]?.every((dataRegion) => {
					return productDataRegions?.includes(dataRegion);
				});
				return !!isProductEligible;
			})
			.reduce((prev, curr) => prev && curr);
	};

const containsExperience = (experience?: ExperimentExperience) => (dynamicExp: LayerFunction) => {
	if (!dynamicExp.experiences || dynamicExp.experiences.length === 0) {
		return true;
	}

	return dynamicExp.experiences?.find(
		(dynamicExpExperience) => experience === dynamicExpExperience,
	);
};

const containsTenantId = (tenantId?: string) => (dynamicExp: LayerFunction) => {
	if (!dynamicExp.tenantIds || dynamicExp.tenantIds.length === 0) {
		return true;
	}

	return dynamicExp.tenantIds?.find((dynamicExpTenantId) => tenantId === dynamicExpTenantId);
};

export const contains3pTarget =
	(thirdPartyFilters?: ThirdPartyFiltersForExperiment) =>
	(dynamicExp: LayerFunction): boolean => {
		if (
			!dynamicExp.connectorSources?.length &&
			!dynamicExp.providerId &&
			!dynamicExp.integrationAri
		) {
			return true;
		}

		if (!thirdPartyFilters?.thirdPartyProducts) {
			return false;
		}

		if (!fg('rovo_3p_exp_targeting')) {
			return false;
		}

		const providerIdExp = dynamicExp.providerId ?? WILDCARD;
		const integrationAriExp = dynamicExp.integrationAri ?? WILDCARD;
		const connectorSourceExp = dynamicExp.connectorSources;

		const matches3pTarget = thirdPartyFilters.thirdPartyProducts.find((thirdPartyProduct) => {
			// https://atlassian.slack.com/archives/C08CGNZTMC2/p1756690283247959
			const containsConnectorSource = thirdPartyProduct.connectorSources?.find(
				(connectorSourceFromConfig) =>
					connectorSourceExp?.find(
						(connectorSourceFromExp) => connectorSourceFromExp === connectorSourceFromConfig,
					),
			);

			const matchesProviderId =
				providerIdExp === WILDCARD || providerIdExp === thirdPartyProduct.providerId;
			const matchesIntegrationAri =
				integrationAriExp === WILDCARD || integrationAriExp === thirdPartyProduct.integrationId;

			return (
				matchesProviderId &&
				matchesIntegrationAri &&
				(!connectorSourceExp || Boolean(containsConnectorSource)) // !connectorSources = wildcard
			);
		});

		return Boolean(matches3pTarget);
	};
